import { Injectable, computed, signal } from '@angular/core';
import { Router } from '@angular/router';

import type { AuthUser } from '@packages/shared-types';
import { AuthApi, HttpError } from '@packages/shared-utils';
import type { TokenPair, TokenProvider } from '@packages/shared-utils';

import { environment } from '../../../environments/environment';
import { RefreshTokenStore } from './refresh-token.store';

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
};

@Injectable({ providedIn: 'root' })
export class AuthService implements TokenProvider {
  private readonly api: AuthApi;

  // Access token is memory-only.
  private accessToken: string | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly state = signal<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  readonly user = computed(() => this.state().user);
  readonly loading = computed(() => this.state().loading);
  readonly error = computed(() => this.state().error);
  readonly isAuthenticated = computed(() => !!this.state().user);

  private readonly ready: Promise<void>;

  constructor(
    private readonly refreshTokenStore: RefreshTokenStore,
    private readonly router: Router,
  ) {
    this.api = new AuthApi(environment.apiBaseUrl, {
      clientType: 'web',
      tokenProvider: this,
    });

    this.ready = this.init();
  }

  private async init(): Promise<void> {
    this.state.update((s) => ({ ...s, loading: true, error: null }));

    try {
      const refreshToken = await this.refreshTokenStore.get();
      if (refreshToken) {
        await this.refresh(refreshToken);
      }
    } catch (err: any) {
      console.error(err);
      this.state.update((s) => ({
        ...s,
        user: null,
        error: this.humanizeError(err) ?? 'Auth init failed',
      }));
    } finally {
      this.state.update((s) => ({ ...s, loading: false }));
    }
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  async refreshSession(): Promise<boolean> {
    try {
      const refreshToken = await this.refreshTokenStore.get();
      if (!refreshToken) return false;

      await this.refresh(refreshToken);
      return !!this.accessToken;
    } catch (err) {
      console.warn('[OperatorAuth] Session refresh failed', err);
      await this.clearTokens();
      return false;
    }
  }

  async signIn(email: string, password: string, returnUrl?: string): Promise<void> {
    this.state.update((s) => ({ ...s, loading: true, error: null }));

    try {
      const resp = await this.api.signIn({ email, password, audience: 'admin' });

      this.accessToken = resp.session.accessToken;
      await this.refreshTokenStore.set(resp.session.refreshToken);

      await this.fetchMe();

      // Safety: backend already enforces allowlist (ADMIN/SUPERADMIN only).
      if (!this.user()) throw new Error('Not authorized for operator panel');

      await this.router.navigateByUrl(returnUrl || '/available', { replaceUrl: true });
    } catch (err: any) {
      console.error(err);
      this.state.update((s) => ({
        ...s,
        user: null,
        error: this.humanizeError(err) ?? 'Sign-in failed',
      }));
      throw err;
    } finally {
      this.state.update((s) => ({ ...s, loading: false }));
    }
  }

  async signOut(): Promise<void> {
    this.state.update((s) => ({ ...s, loading: true, error: null }));

    let refreshToken: string | null = null;
    try {
      refreshToken = await this.refreshTokenStore.get();
    } catch {
      // ignore
    }

    // Best-effort remote revoke.
    try {
      if (refreshToken) {
        await this.api.signOut({ refreshToken });
      }
    } catch (err) {
      console.warn('[Auth] Remote signOut failed; clearing local session anyway', err);
    }

    await this.clearTokens();

    this.state.update((s) => ({ ...s, user: null, loading: false, error: null }));
    await this.router.navigateByUrl('/auth/sign-in', { replaceUrl: true });
  }

  async requestPasswordReset(email: string): Promise<void> {
    this.state.update((s) => ({ ...s, loading: true, error: null }));
    try {
      await this.api.requestPasswordReset(email);
    } catch (err: any) {
      console.error(err);
      this.state.update((s) => ({
        ...s,
        error: this.humanizeError(err) ?? 'Password reset request failed',
      }));
      throw err;
    } finally {
      this.state.update((s) => ({ ...s, loading: false }));
    }
  }

  async confirmPasswordReset(accessToken: string, newPassword: string): Promise<void> {
    this.state.update((s) => ({ ...s, loading: true, error: null }));
    try {
      await this.api.confirmPasswordReset(accessToken, newPassword);
    } catch (err: any) {
      console.error(err);
      this.state.update((s) => ({
        ...s,
        error: this.humanizeError(err) ?? 'Password reset failed',
      }));
      throw err;
    } finally {
      this.state.update((s) => ({ ...s, loading: false }));
    }
  }

  // --- TokenProvider (shared-utils) ---

  getAccessToken(): string | null {
    return this.accessToken;
  }

  async getRefreshToken(): Promise<string | null> {
    return this.refreshTokenStore.get();
  }

  async setTokens(tokens: TokenPair): Promise<void> {
    this.accessToken = tokens.accessToken;
    await this.refreshTokenStore.set(tokens.refreshToken);
  }

  async clearTokens(): Promise<void> {
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
    await this.refreshTokenStore.clear();
    this.accessToken = null;
    this.state.update((s) => ({ ...s, user: null }));
  }

  // --- Internal ---

  private async refresh(existingRefreshToken: string): Promise<void> {
    const resp = await this.api.refresh({ refreshToken: existingRefreshToken });
    this.accessToken = resp.session.accessToken;
    await this.refreshTokenStore.set(resp.session.refreshToken);
    await this.fetchMe();
    if (this.accessToken) this.scheduleProactiveRefresh(this.accessToken);
  }

  private scheduleProactiveRefresh(token: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    try {
      const parts = token.split('.');
      if (parts.length < 2) return;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
      const payload = JSON.parse(atob(padded));
      const exp: number | undefined = payload?.exp;
      if (!exp) return;
      const msUntilRefresh = exp * 1000 - Date.now() - 5 * 60 * 1000;
      if (msUntilRefresh <= 0) return;
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        void this.refreshSession();
      }, msUntilRefresh);
    } catch {
      // ignore malformed tokens
    }
  }

  private async fetchMe(): Promise<void> {
    try {
      if (!this.accessToken) throw new Error('Missing access token');
      const resp = await this.api.me();
      const role = resp.user.role;
      if (role !== 'ADMIN' && role !== 'SUPERADMIN') {
        // Defensive: if backend config changes, still prevent UI access.
        await this.clearTokens();
        this.state.update((s) => ({ ...s, user: null }));
        return;
      }
      this.state.update((s) => ({ ...s, user: resp.user as AuthUser }));
    } catch {
      this.state.update((s) => ({ ...s, user: null }));
    }
  }

  clearError(): void {
    this.state.update((s) => ({ ...s, error: null }));
  }

  humanizeError(err: any): string {
    if (err instanceof HttpError) {
      if (typeof err.body === 'string' && err.body.trim().length > 0) return err.body;
      if (err.body && typeof err.body === 'object') {
        const msg = (err.body as any)?.message;
        if (typeof msg === 'string') return msg;
      }
      return `HTTP ${err.status}`;
    }
    if (err instanceof Error) return err.message;
    return 'Unknown error';
  }
}
